/**
 * @documental
 * @export Icon
 * @import
 * import { Icon } from "capitalrx-components-2.0/lib/general";
 */

import { FC, ReactNode, RefCallback } from "react";
import classNames from "classnames";
import { AccessibilityProps } from "capitalrx-components-2.0/lib/AccessibilityProps";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import styled from "styled-components";
import { Tooltip } from "../Tooltip";
import { IconName } from "./IconName";
import { IconSize } from "./IconSize";
import { IconColor } from "./IconColor";
import { getIconComponent } from "./getIconComponent";
import * as Color from "../Color";
import styles from "./styles.module.scss";
import { getColorFromIconColor } from "./getColorFromIconColor";
import { formatIconName } from "./formatIconName";

/**
 * The `Icon` component properties.
 */

export interface IconProps extends BaseProps, Pick<AccessibilityProps, "aria-label" | "aria-hidden" > {
  /**
   * The icon name. See below for a list of available icons and their names.
   */
  readonly name: IconName;

  /**
   * The size of the icon. If not specified defaults to `"regular"`.
   * icons defaults to regular size
   */
  readonly size?: IconSize;

  /**
   * The icon color. If not specified defaults to `"black"`.
   */
  readonly color?: IconColor | Color.RgbColor;

  /**
   * The icon tooltip.
   */
  readonly tooltip?: ReactNode;

  /**
   * Indicates whether the icon is decorative or conveys meaning. Defaults to `false`.
   */
  readonly isDecorative?: boolean;
}

const IconContainer = styled.span<{ iconColor: Color.RgbColor }>`
  path,
  circle,
  g {
    fill: ${(props) => Color.toString(props.iconColor)};
  }
`;

/**
 * Displays a scalable icon with the specified size and color.
 */
export const Icon: FC<IconProps> = (props) => {
  const { name, size = "regular", color = Color.DarkGrey[500], tooltip, "aria-label" : ariaLabel, "aria-hidden" : ariaHidden = false, isDecorative = false } = props;

  const iconColor = typeof color === "string" ? getColorFromIconColor(color) : color;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Icon,
      "icon",
      `size-${size}`,
      {
        "has-tooltip": !!tooltip,
      },
    ],
    testIdPrefix: "icon",
  });

  const Component = getIconComponent(name, size);
  const computedRole = isDecorative ? "presentation" : "img";
  const computedLabel = ariaLabel || (name ? `${formatIconName(name)} Icon` : "Icon");
  const computedVisibility = isDecorative ? true : ariaHidden ?? false;

  const content = (ref?: RefCallback<HTMLElement>) => (
    <span ref={ref} {...baseHtmlProps} >
      <IconContainer className={classNames(styles.IconContainer, "icon-container")} iconColor={iconColor}>
        {name === "none" && <></>}
        {name !== "none" && Component !== null && <Component title={computedLabel} aria-hidden={computedVisibility} role={computedRole} focusable="false" />}
      </IconContainer>
    </span>
  );

  return (
    <>
      {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
      {!tooltip && content()}
    </>
  );
};
